/**
 * Force a `<details>` open while the page is printing, then restore it.
 *
 * ## Why this is JS and not a stylesheet rule
 *
 * A closed `<details>` hides its content through the user agent's own shadow slot. `display: block` in
 * a print stylesheet does **not** reliably reopen it — the content is not merely hidden by CSS we
 * control, so a print rule has nothing to override. Setting `el.open` around the print lifecycle is
 * the only portable answer.
 *
 * ## Why it is SHARED
 *
 * It was inline in `ChartTable`, which meant the next collapsible section had to know the trick
 * existed. One did not: the report's six category-breakdown rows shipped without it, and Chad's first
 * UAT pass (Hazel Street, 2026-07-29) caught the consequence — an exported PDF whose Findings section
 * is six chevrons and several blank pages. The PDF is the artifact a client forwards and an external
 * reviewer reads, so a section that silently prints empty is not a cosmetic defect.
 *
 * Extracting it makes the correct behaviour the reusable thing rather than a detail one component
 * happened to remember.
 */

import { useCallback, useRef } from 'react';

/** The one property this needs from a `<details>`. Narrowed so the behaviour is testable without a DOM. */
export interface PrintExpandable {
  open: boolean;
}

/** The slice of `window` this needs. Same reason. */
export interface PrintEventTarget {
  addEventListener(type: string, handler: () => void): void;
  removeEventListener(type: string, handler: () => void): void;
}

/**
 * Wire `el` to `target`'s print lifecycle; returns the unsubscribe.
 *
 * Separated from the hook so the BEHAVIOUR has a real test. The web package runs vitest in the node
 * environment with no jsdom, and an SSR render cannot observe this at all — a `<details>` keeps its
 * content in the DOM whether open or closed, so a markup assertion passes identically against the bug.
 * A test that cannot fail against the broken version is not a test, so the logic lives here where a
 * fake element and a fake target can prove it.
 *
 * The prior state is captured at `beforeprint` and restored at `afterprint`, so printing never
 * silently changes what the reader had open on screen.
 */
export function bindPrintExpansion(el: PrintExpandable, target: PrintEventTarget): () => void {
  let wasOpen = false;
  const before = (): void => {
    wasOpen = el.open;
    el.open = true;
  };
  const after = (): void => {
    el.open = wasOpen;
  };
  target.addEventListener('beforeprint', before);
  target.addEventListener('afterprint', after);
  return () => {
    target.removeEventListener('beforeprint', before);
    target.removeEventListener('afterprint', after);
  };
}

/**
 * Ref for a `<details>` that must be expanded in print. Attach it and nothing else is needed.
 *
 * ⚠️ A CALLBACK REF, NOT `useRef` + `useEffect` (#1157 red-team). The effect version read
 * `ref.current` once, under `[]` deps, and bailed when it was null — so any `<details>` that mounts
 * LATER than its hook never got bound, silently and for good. That is not a corner case: a panel
 * renders nothing until it has rows (`Variances`, `RecordFacts`), and its disclosures therefore
 * always attach after the first commit. The very PDF this hook exists to protect — Chad's Hazel
 * Street case above — printed as chevrons again on exactly that path.
 *
 * React calls a callback ref with the element on attach and with `null` on detach, so the binding
 * tracks the element instead of the first render. The unsubscribe is held across calls and run
 * before each re-bind, so an element that remounts never leaves a stale listener on `window`.
 */
export function usePrintExpanded(): (el: HTMLDetailsElement | null) => void {
  const unbind = useRef<(() => void) | null>(null);
  return useCallback((el: HTMLDetailsElement | null) => {
    unbind.current?.();
    unbind.current = el ? bindPrintExpansion(el, window) : null;
  }, []);
}
